import { useState } from "react";
import { RotateCcw, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { GroupInfo } from "@/interfaces/user.interface";
import useUserApiService from "@/services/user.service";
import ConfirmationDialog from "../modal/confirmationModal";
import { UNLIMITED_TOKEN } from "@/constants/user-table.constants";

/**
 * Props for the GroupUsagePanel component
 */
interface GroupUsagePanelProps {
  /** User ID */
  userId: number;
  /** Array of group information with usage data */
  groups: GroupInfo[];
}

/**
 * GroupUsagePanel component displays group usage information for a user with
 * token usage, limits, and reset functionality. Shows confirmation dialog before
 * resetting tokens. Displays loading state during reset operation.
 * 
 * @component
 * @param {GroupUsagePanelProps} props - Component props
 * @param {number} props.userId - User ID
 * @param {GroupInfo[]} props.groups - Group information array
 * @returns {JSX.Element} Group usage panel with usage stats and reset buttons
 * 
 * @example
 * ```tsx
 * <GroupUsagePanel
 *   userId={123}
 *   groups={userGroups}
 * />
 * ```
 */
export default function GroupUsagePanel({
  userId,
  groups,
}: GroupUsagePanelProps) {
  const [localGroups, setLocalGroups] = useState(groups);
  const [confirmGroup, setConfirmGroup] = useState<GroupInfo | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const { resetUserTokens } = useUserApiService();

  const handleReset = async (group: GroupInfo) => {
    try {
      setLoadingId(group.model_id!);

      const response = await resetUserTokens(userId, group.model_id!);

      const updated = localGroups.map((g) =>
        g.model_id === group.model_id ? { ...g, usage: response.data } : g
      );

      setLocalGroups(updated);
      toast.success("Tokens reset successfully!");
    } catch (err) {
      toast.error("Failed to reset tokens");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <ConfirmationDialog
        open={!!confirmGroup}
        title={`Reset usage tokens for ${confirmGroup?.group_name}?`}
        description={`This will reset the used tokens for this user from ${confirmGroup?.usage?.used_tokens} to 0.`}
        confirmText="Reset Tokens"
        cancelText="Cancel"
        onCancel={() => setConfirmGroup(null)}
        onConfirm={() => {
          if (confirmGroup) handleReset(confirmGroup);
          setConfirmGroup(null);
        }}
      />
      
      {/* Group Cards */}
      <div className="flex flex-row gap-2 border border-gray-100 p-4 px-14 flex-wrap">
        {localGroups.length === 0 && (
          <div className="w-full text-center text-sm text-gray-500">
            No groups assigned to this user.
          </div>
          )}
        
        {localGroups.map((group) => {
          const usage = group.usage;

          return (
            <div
              key={group.model_id}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="pr-8">
                  <div className="text-sm font-semibold text-gray-900">
                    {group.group_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    Model: <span className="font-medium">{group.name}</span>
                  </div>
                </div>

                {/* Reset Button */}
                {group?.usage?.used_tokens &&
                group?.monthly_credits !== UNLIMITED_TOKEN ? (
                  <button
                    onClick={() => setConfirmGroup(group)}
                    className="flex items-center gap-1 text-xxs border border-primary-600 text-primary-600 rounded px-2 py-1 hover:bg-blue-50 transition"
                    disabled={loadingId === group.model_id}
                  >
                    {loadingId === group.model_id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RotateCcw className="w-3 h-3" />
                    )}
                    Reset
                  </button>
                ) : null}
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <InfoItem
                  label="Usage"
                  value={`${usage?.used_tokens ?? 0} / ${
                    group.monthly_credits
                      ? group.monthly_credits == UNLIMITED_TOKEN
                        ? "unlimited"
                        : group.monthly_credits
                      : 0
                  }`}
                />
                <InfoItem
                  label="Reset Count"
                  value={usage?.reset_count ?? "—"}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function InfoItem({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-gray-500">{label}:</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
}
